import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheck, FaBuilding, FaMapMarkerAlt, FaUsers, FaClock, FaUserPlus, FaCheckCircle, FaArrowRight, FaArrowLeft, FaPlus, FaTrash, FaExclamationCircle, FaInfoCircle, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { MdError } from 'react-icons/md';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import TeamMembers from './TeamMembers';

const LOCAL_STORAGE_KEY = 'registrationStepperFormData';

// Helper to generate unique IDs
function generateId() {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
}

// Tooltip component
const Tooltip = ({ text }) => (
  <span className="relative group cursor-pointer ml-1">
    <FaInfoCircle className="inline text-gray-400 hover:text-primary" />
    <span className="absolute left-1/2 -translate-x-1/2 mt-2 w-48 bg-black text-white text-xs rounded p-2 opacity-0 group-hover:opacity-100 pointer-events-none z-50 transition-opacity duration-200">
      {text}
    </span>
  </span>
);

// Helper to parse 12-hour time with AM/PM
function parseTime12(str) {
  if (!str) return { h: 0, m: 0 };
  const [time, period] = str.split(' ');
  let [h, m] = time.split(':').map(Number);
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return { h, m };
}

const RegistrationStepper = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    const initialPin = Math.floor(1000 + Math.random() * 9000).toString();
    return saved ? JSON.parse(saved) : {
      // Step 1
      business_name: '',
      admin_name: '',
      email: '',
      mobile_number: '',
      country: '',
      business_type: '',
      industry: '',
      employee_count: '',

      // Step 2
      has_multiple_locations: 'single',
      locations: [{ name: '', address: '' }],

      // Step 3
      roles: [{ name: 'Admin' }, { name: 'Manager' }],

      // Step 4
      week_start_day: 'Sun',
      default_shift_start: '05:00 AM',
      default_shift_end: '05:00 PM',
      default_break_duration: '30',

      // Step 5
      team_members: [{
        name: '',
        lastName: '',
        pin: initialPin,
        phone: '',
        level: 'Admin',
        is_admin: true,
      }]
    };
  });

  // Add state for address suggestions
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressTimeout = useRef();

  // Add state to track which location index is being edited
  const [activeAddressIndex, setActiveAddressIndex] = useState(null);

  // Add state to track open location index
  const [openLocationIndex, setOpenLocationIndex] = useState(0);

  // Add state to track open team member index
  const [openTeamMemberIndex, setOpenTeamMemberIndex] = useState(0);

  // Save formData to localStorage on change
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  // Auto-detect country
  useEffect(() => {
    // Try browser locale first
    let browserCountry = '';
    if (navigator.languages && navigator.languages.length > 0) {
      const locale = navigator.languages[0];
      if (locale.includes('-')) browserCountry = locale.split('-')[1];
    } else if (navigator.language) {
      const locale = navigator.language;
      if (locale.includes('-')) browserCountry = locale.split('-')[1];
    }
    if (browserCountry) {
      setFormData(prev => ({ ...prev, country: browserCountry.toUpperCase() }));
    } else {
      // Fallback to IP geolocation
      fetch('https://ipapi.co/json/')
        .then(res => res.json())
        .then(data => {
          if (data.country_code) {
            setFormData(prev => ({
              ...prev,
              country: data.country_code
            }));
          }
        })
        .catch(err => console.error('Error detecting country:', err));
    }
  }, []);

  // Ensure all locations have a storeID on load
  useEffect(() => {
    setFormData(prev => {
      if (!prev.locations) return prev;
      let changed = false;
      const locations = prev.locations.map(loc => {
        if (!loc.storeID) {
          changed = true;
          const { id, ...rest } = loc;
          return { ...rest, storeID: loc.id || generateId() };
        }
        return loc;
      });
      return changed ? { ...prev, locations } : prev;
    });
  }, []);

  // Helper to get country code for Nominatim
  const getCountryCode = () => (formData.country || '').toLowerCase();

  // Address autocomplete handler
  const handleAddressInput = (value, index) => {
    setActiveAddressIndex(index);
    // Update the address in formData
    const newLocations = [...formData.locations];
    newLocations[index].address = value;
    setFormData(prev => ({ ...prev, locations: newLocations }));

    // Debounce API calls
    if (addressTimeout.current) clearTimeout(addressTimeout.current);
    if (!value) {
      setAddressSuggestions([]);
      return;
    }
    setAddressLoading(true);
    addressTimeout.current = setTimeout(() => {
      fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(value)}&countrycodes=${getCountryCode()}&format=json&addressdetails=1&limit=5`)
        .then(res => res.json())
        .then(data => {
          setAddressSuggestions(data);
          setAddressLoading(false);
        })
        .catch(() => setAddressLoading(false));
    }, 400);
  };

  const steps = [
    { number: 1, title: 'Business Details', icon: FaBuilding },
    { number: 2, title: 'Locations Setup', icon: FaMapMarkerAlt },
    { number: 3, title: 'Areas & Roles', icon: FaUsers },
    { number: 4, title: 'Schedule Settings', icon: FaClock },
    { number: 5, title: 'Add Team Members', icon: FaUserPlus }
  ];

  const validateStep = () => {
    const newErrors = {};
    switch (currentStep) {
      case 1:
        if (!formData.business_name) newErrors.business_name = 'Business name is required';
        if (!formData.admin_name) newErrors.admin_name = 'Admin name is required';
        if (!formData.email) {
          newErrors.email = 'Email is required';
        } else {
          // Basic email regex validation
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Enter a valid email address';
          }
        }
        if (!formData.mobile_number) {
          newErrors.mobile_number = 'Mobile number is required';
        } else {
          // Validate using libphonenumber-js
          try {
            const phone = parsePhoneNumberFromString('+' + formData.mobile_number, formData.country);
            if (!phone || !phone.isValid()) {
              newErrors.mobile_number = 'Enter a valid mobile number for your country';
            }
          } catch {
            newErrors.mobile_number = 'Enter a valid mobile number for your country';
          }
        }
        if (!formData.business_type) newErrors.business_type = 'Business type is required';
        if (!formData.industry) newErrors.industry = 'Industry is required';
        if (!formData.employee_count) newErrors.employee_count = 'Employee count is required';
        break;

      case 2:
        if (formData.locations.some(loc => !loc.name)) {
          newErrors.locations = 'All locations must have a name';
        }
        if (formData.locations.some(loc => !loc.address)) {
          newErrors.locations = 'All locations must have an address';
        }
        break;

      case 3:
        if (formData.roles.some(role => !role.name)) {
          newErrors.roles = 'All roles must have a name';
        }
        break;

      case 4:
        // 5am-5am strict logic using Date objects and proper date comparisons
        if (formData.default_shift_start && formData.default_shift_end) {
          const { h: startH, m: startM } = parseTime12(formData.default_shift_start);
          const { h: endH, m: endM } = parseTime12(formData.default_shift_end);

          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

          // Create start time for today
          const startTime = new Date(today);
          startTime.setHours(startH, startM, 0, 0);

          // Create end time (initially for today, will adjust if needed)
          const endTime = new Date(today);
          endTime.setHours(endH, endM, 0, 0);

          // If end time is <= start time, it means it crosses midnight (next day)
          // OR if end time is in early morning hours (1-5 AM), treat as next day
          if (endTime <= startTime || (endH >= 1 && endH <= 5)) {
            endTime.setDate(endTime.getDate() + 1);
          }

          // Create boundary times
          const todayFiveAM = new Date(today);
          todayFiveAM.setHours(5, 0, 0, 0);

          const nextDayFiveAM = new Date(today);
          nextDayFiveAM.setDate(nextDayFiveAM.getDate() + 1);
          nextDayFiveAM.setHours(5, 0, 0, 0);

          // Rule 1: Start time cannot be before today 5:00 AM
          if (startTime < todayFiveAM) {
            newErrors.default_shift_start = 'Shift cannot start before 5:00 AM.';
          }

          // Rule 2: End time cannot be after next day 5:00 AM (must be <= next day 5:00 AM)
          if (endTime > nextDayFiveAM) {
            newErrors.default_shift_end = 'If shift crosses midnight, it must end by 5:00 AM (not after).';
          }

          // Rule 3: Same day shifts are automatically valid if they pass Rule 1
          // (No additional validation needed since same day end times are always before next day 5 AM)
        }
        break;

      case 5:
        const memberErrors = {};
        const weakPins = [
          '0000', '1111', '2222', '3333', '4444', '5555', '6666', '7777', '8888', '9999',
          '1234', '4321', '1212', '6969', '1010', '2020', '1122', '3344', '5566', '7788'
        ];
        formData.team_members.forEach((member, idx) => {
          if (!member.name) {
            memberErrors[idx] = memberErrors[idx] || {};
            memberErrors[idx].name = 'Name is required';
          }
          if (!member.level) {
            memberErrors[idx] = memberErrors[idx] || {};
            memberErrors[idx].level = 'Level is required';
          }
          if (!member.pin || member.pin.length !== 4) {
            memberErrors[idx] = memberErrors[idx] || {};
            memberErrors[idx].pin = 'PIN must be exactly 4 digits';
          } else if (!/^[0-9]{4}$/.test(member.pin)) {
            memberErrors[idx] = memberErrors[idx] || {};
            memberErrors[idx].pin = 'PIN must be 4 digits';
          } else if (weakPins.includes(member.pin)) {
            memberErrors[idx] = memberErrors[idx] || {};
            memberErrors[idx].pin = 'PIN is too weak. Choose a less obvious PIN.';
          }
          if (member.phone) {
            try {
              const raw = member.phone || '';
              const phone = parsePhoneNumberFromString(raw.startsWith('+') ? raw : `+${raw}`);
              if (!phone || !phone.isValid()) {
                memberErrors[idx] = memberErrors[idx] || {};
                memberErrors[idx].phone = 'Invalid phone number for selected country';
              }
            } catch {
              memberErrors[idx] = memberErrors[idx] || {};
              memberErrors[idx].phone = 'Invalid phone number for selected country';
            }
          }
        });
        if (Object.keys(memberErrors).length > 0) {
          newErrors.team_members = memberErrors;
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    // Always sync admin name to team_members[0].name before validation
    setFormData(prev => ({
      ...prev,
      team_members: prev.team_members.map((m, i) =>
        i === 0 ? { ...m, name: prev.admin_name } : m
      ),
    }));
    // Wait for state to update before continuing
    setTimeout(async () => {
      if (currentStep === 1) {
        const valid = validateStep();
        if (!valid) return;
        setIsLoading(true);
        setIsCheckingPhone(true);
        try {
          // Format phone number: remove country code and ensure it starts with 0
          const phoneNumber = parsePhoneNumberFromString('+' + formData.mobile_number, formData.country);
          const nationalNumber = phoneNumber.nationalNumber;
          const formattedPhone = nationalNumber.startsWith('0') ? nationalNumber : '0' + nationalNumber;

          const res = await fetch('https://us-central1-timeclock-f2c1a.cloudfunctions.net/checkCustomerPhoneExists', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Access-Control-Allow-Origin': '*',
              'Access-Control-Allow-Methods': 'POST, OPTIONS',
              'Access-Control-Allow-Headers': 'Content-Type'
            },
            mode: 'cors',
            credentials: 'omit',
            body: JSON.stringify({ phone: formattedPhone }),
          });
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          const data = await res.json();
          if (data.exists === true) {
            setErrors(prev => ({
              ...prev,
              mobile_number: 'This phone number is already registered. Please login instead.',
            }));
            setIsCheckingPhone(false);
            setIsLoading(false);
            return;
          }
          // If exists is false, clear any phone error and proceed
          setErrors(prev => ({ ...prev, mobile_number: undefined }));
          setIsCheckingPhone(false);
          setIsLoading(false);
          setCurrentStep(currentStep + 1);
          return;
        } catch (err) {
          setErrors(prev => ({
            ...prev,
            mobile_number: 'Could not verify phone number. Please try again.',
          }));
          setIsCheckingPhone(false);
          setIsLoading(false);
          return;
        }
      }
      const valid = validateStep();
      if (valid) {
        if (currentStep === 5) {
          setIsLoading(true);
          setIsRegistering(true);
          try {
            // Format all phone numbers: remove country code and ensure they start with 0
            const formatPhoneNumber = (phone, country) => {
              if (!phone) return '';
              const phoneNumber = parsePhoneNumberFromString('+' + phone, country);
              if (!phoneNumber) return '';
              const nationalNumber = phoneNumber.nationalNumber;
              return nationalNumber.startsWith('0') ? nationalNumber : '0' + nationalNumber;
            };

            const formattedData = {
              ...formData,
              mobile_number: formatPhoneNumber(formData.mobile_number, formData.country),
              default_break_duration: formData.default_break_duration === 'custom'
                ? formData.custom_break_duration
                : formData.default_break_duration,
              // Convert times to 24-hour format
              default_shift_start: (() => {
                const { h, m } = parseTime12(formData.default_shift_start);
                return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
              })(),
              default_shift_end: (() => {
                const { h, m } = parseTime12(formData.default_shift_end);
                return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
              })(),
              team_members: formData.team_members.map(member => ({
                ...member,
                phone: member.phone ? formatPhoneNumber(member.phone, formData.country) : ''
              }))
            };

            const res = await fetch('https://us-central1-timeclock-f2c1a.cloudfunctions.net/registerCustomer', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Methods': 'POST, OPTIONS',
                'Access-Control-Allow-Headers': 'Content-Type'
              },
              mode: 'cors',
              credentials: 'omit',
              body: JSON.stringify(formattedData)
            });

            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

            // Clear local storage and redirect to login
            localStorage.removeItem(LOCAL_STORAGE_KEY);
            window.location.href = 'https://clockinmaster.com/auth/login';
          } catch (err) {
            console.error('Registration error:', err);
            setErrors(prev => ({
              ...prev,
              registration: 'Registration failed. Please try again.',
            }));
          } finally {
            setIsRegistering(false);
            setIsLoading(false);
          }
        } else {
          setCurrentStep(currentStep + 1);
        }
      }
    }, 0);
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (value, country) => {
    let v = value;
    // Optionally strip leading 0 (uncomment if you want)
    // if (v.startsWith('0')) v = v.slice(1);
    setFormData(prev => ({
      ...prev,
      mobile_number: v,
      country: country.countryCode
    }));
  };

  const generatePin = () => {
    return Math.floor(1000 + Math.random() * 9000).toString();
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.3
      }
    }
  };

  const getProgressPercent = () => {
    // Count filled fields for current step
    let total = 0, filled = 0;
    if (currentStep === 1) {
      total = 6;
      if (formData.business_name) filled++;
      if (formData.admin_name) filled++;
      if (formData.email) filled++;
      if (formData.mobile_number) filled++;
      if (formData.business_type) filled++;
      if (formData.industry) filled++;
      if (formData.employee_count) filled++;
    }
    // You can expand for other steps if you want per-step progress
    return Math.round((filled / total) * 100);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: {
        const inputClass = "w-full px-4 py-3 rounded-xl bg-gray-50 border-2 border-gray-200 focus:border-secondary focus:bg-white focus:ring-4 focus:ring-secondary/8 text-gray-900 text-sm transition-all duration-300 outline-none placeholder-gray-400 hover:border-gray-300 hover:bg-white";
        const selectClass = inputClass + " appearance-none cursor-pointer bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236b7280%22%20d%3D%22M2%204l4%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[position:right_16px_center] pr-10";
        const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";
        const errorClass = "text-red-500 text-xs mt-1.5 font-medium";
        return (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-5"
          >
            {/* Two-column layout for name fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <motion.div variants={itemVariants}>
                <label className={labelClass}>Business Name</label>
                <input
                  type="text"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleInputChange}
                  placeholder="e.g. Acme Corporation"
                  className={`${inputClass} ${errors.business_name ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : ''}`}
                />
                {errors.business_name && <p className={errorClass}>{errors.business_name}</p>}
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className={labelClass}>Admin Name</label>
                <input
                  type="text"
                  name="admin_name"
                  value={formData.admin_name}
                  onChange={handleInputChange}
                  placeholder="e.g. John Smith"
                  className={`${inputClass} ${errors.admin_name ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : ''}`}
                />
                {errors.admin_name && <p className={errorClass}>{errors.admin_name}</p>}
              </motion.div>
            </div>

            <motion.div variants={itemVariants}>
              <label className={labelClass}>Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="john@yourcompany.com"
                className={`${inputClass} ${errors.email ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : ''}`}
              />
              {errors.email && <p className={errorClass}>{errors.email}</p>}
            </motion.div>

            <motion.div variants={itemVariants}>
              <label className={labelClass}>Mobile Number</label>
              <PhoneInput
                country={(formData.country || '').toLowerCase()}
                value={formData.mobile_number}
                onChange={handlePhoneChange}
                containerClass="phone-input-container"
                enableSearch={true}
                searchPlaceholder="Search country..."
                searchNotFound="No country found"
                preferredCountries={['us', 'gb', 'ca', 'au', 'in']}
              />
              {errors.mobile_number && <p className={errorClass}>{errors.mobile_number}</p>}
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <motion.div variants={itemVariants}>
                <label className={labelClass}>Business Type</label>
                <select
                  name="business_type"
                  value={formData.business_type}
                  onChange={handleInputChange}
                  className={`${selectClass} ${errors.business_type ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : ''}`}
                >
                  <option value="">Select business type</option>
                  <option value="retail">Retail & Hospitality</option>
                  <option value="services">Services</option>
                  <option value="healthcare">Health Care</option>
                  <option value="charity">Charity</option>
                  <option value="other">Other</option>
                </select>
                {errors.business_type && <p className={errorClass}>{errors.business_type}</p>}
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className={labelClass}>Industry</label>
                {formData.business_type === 'other' ? (
                  <input
                    type="text"
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    placeholder="Enter your industry"
                    className={`${inputClass} ${errors.industry ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : ''}`}
                  />
                ) : (
                  <select
                    name="industry"
                    value={formData.industry}
                    onChange={handleInputChange}
                    className={`${selectClass} ${errors.industry ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : ''}`}
                  >
                    <option value="">Select industry</option>
                    {formData.business_type === 'retail' && (
                      <>
                        <option value="food">Food/Fast Food & Beverage</option>
                        <option value="fashion">Fashion & Apparel</option>
                        <option value="grocery">Grocery & Supermarkets</option>
                        <option value="hotels">Hotels & Accommodation</option>
                        <option value="tourism">Travel & Tourism</option>
                        <option value="entertainment">Entertainment & Leisure</option>
                        <option value="ecommerce">E-commerce Retail</option>
                      </>
                    )}
                    {formData.business_type === 'services' && (
                      <>
                        <option value="consulting">Consulting</option>
                        <option value="education">Education & Training</option>
                        <option value="financial">Financial Services</option>
                        <option value="legal">Legal Services</option>
                        <option value="marketing">Marketing & Advertising</option>
                        <option value="it">IT & Software Services</option>
                      </>
                    )}
                    {formData.business_type === 'healthcare' && (
                      <>
                        <option value="hospital">Hospital</option>
                        <option value="clinic">Clinic</option>
                        <option value="pharmacy">Pharmacy</option>
                        <option value="dental">Dental Practice</option>
                        <option value="wellness">Wellness Center</option>
                      </>
                    )}
                    {formData.business_type === 'charity' && (
                      <>
                        <option value="ngo">NGO</option>
                        <option value="foundation">Foundation</option>
                        <option value="community">Community Service</option>
                        <option value="education">Educational Charity</option>
                        <option value="health">Health & Medical Charity</option>
                      </>
                    )}
                  </select>
                )}
                {errors.industry && <p className={errorClass}>{errors.industry}</p>}
              </motion.div>
            </div>

            <motion.div variants={itemVariants}>
              <label className={labelClass}>Employee Count</label>
              <select
                name="employee_count"
                value={formData.employee_count}
                onChange={handleInputChange}
                className={`${selectClass} ${errors.employee_count ? 'border-red-300 focus:border-red-400 focus:ring-red-100' : ''}`}
              >
                <option value="">Select employee count</option>
                <option value="2-5">2-5</option>
                <option value="5-10">5-10</option>
                <option value="10-20">10-20</option>
                <option value="20-30">20-30</option>
                <option value="30+">30+</option>
              </select>
              {errors.employee_count && <p className={errorClass}>{errors.employee_count}</p>}
            </motion.div>
          </motion.div>
        );
      }

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location Type</label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="has_multiple_locations"
                    value="single"
                    checked={formData.has_multiple_locations === 'single'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Just One
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="has_multiple_locations"
                    value="multiple"
                    checked={formData.has_multiple_locations === 'multiple'}
                    onChange={handleInputChange}
                    className="mr-2"
                  />
                  Multiple
                </label>
              </div>
            </div>
            {formData.locations.map((location, index) => (
              <div key={location.storeID || index} className="mb-2 border rounded-xl shadow-sm bg-white">
                <div
                  className="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
                  onClick={() => setOpenLocationIndex(index)}
                >
                  <span className="font-semibold text-primary">{location.name || `Location ${index + 1}`}</span>
                  {formData.locations.length > 1 && (
                    <div className="flex items-center gap-2">
                      <button onClick={e => { e.stopPropagation(); handleDeleteLocation(index); }} className="text-red-500 hover:text-red-700"><FaTrash /></button>
                      {openLocationIndex === index ? <FaChevronUp /> : <FaChevronDown />}
                    </div>
                  )}
                </div>
                {openLocationIndex === index && (
                  <div className="p-4 pt-0 space-y-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                      <input
                        type="text"
                        name={`locations.${index}.name`}
                        value={location.name}
                        onChange={(e) => {
                          const newLocations = [...formData.locations];
                          newLocations[index].name = e.target.value;
                          setFormData(prev => ({ ...prev, locations: newLocations }));
                        }}
                        className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      {getCountryCode() === 'gb' ? (
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                            <input
                              type="text"
                              value={location.postcode || ''}
                              onChange={e => {
                                const newLocations = [...formData.locations];
                                newLocations[index].postcode = e.target.value;
                                setFormData(prev => ({ ...prev, locations: newLocations }));
                                handleAddressInput(e.target.value, index);
                              }}
                              onBlur={() => setTimeout(() => setAddressSuggestions([]), 200)}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                            {activeAddressIndex === index && addressSuggestions.length > 0 && (
                              <ul className="absolute z-10 bg-white border border-gray-200 rounded shadow mt-1 w-full max-h-48 overflow-auto">
                                {addressSuggestions.map(s => (
                                  <li key={s.place_id} className="px-3 py-2 hover:bg-primary/10 cursor-pointer text-sm" onClick={() => {
                                    const details = s.address || {};
                                    const newLocations = [...formData.locations];
                                    newLocations[index].postcode = details.postcode || '';
                                    newLocations[index].doorNumber = details.house_number || '';
                                    newLocations[index].street = details.road || details.pedestrian || details.footway || '';
                                    newLocations[index].city = details.city || details.town || details.village || '';
                                    setFormData(prev => ({ ...prev, locations: newLocations }));
                                    setAddressSuggestions([]);
                                  }}>{s.display_name}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                          {/* Only show the rest if postcode is filled */}
                          {location.postcode && (
                            <>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Door/Flat/Unit Number</label>
                                <input
                                  type="text"
                                  value={location.doorNumber || ''}
                                  onChange={e => {
                                    const newLocations = [...formData.locations];
                                    newLocations[index].doorNumber = e.target.value;
                                    setFormData(prev => ({ ...prev, locations: newLocations }));
                                  }}
                                  className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Street/Road</label>
                                <input
                                  type="text"
                                  value={location.street || ''}
                                  onChange={e => {
                                    const newLocations = [...formData.locations];
                                    newLocations[index].street = e.target.value;
                                    setFormData(prev => ({ ...prev, locations: newLocations }));
                                  }}
                                  className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">City/Town</label>
                                <input
                                  type="text"
                                  value={location.city || ''}
                                  onChange={e => {
                                    const newLocations = [...formData.locations];
                                    newLocations[index].city = e.target.value;
                                    setFormData(prev => ({ ...prev, locations: newLocations }));
                                  }}
                                  className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                                />
                              </div>
                            </>
                          )}
                        </>
                      ) : getCountryCode() === 'us' ? (
                        <>
                          {/* US: Standard address autocomplete */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                            <input
                              type="text"
                              value={location.address || ''}
                              onChange={e => handleAddressInput(e.target.value, index)}
                              onBlur={() => setTimeout(() => setAddressSuggestions([]), 200)}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                            {activeAddressIndex === index && addressSuggestions.length > 0 && (
                              <ul className="absolute z-10 bg-white border border-gray-200 rounded shadow mt-1 w-full max-h-48 overflow-auto">
                                {addressSuggestions.map(s => (
                                  <li key={s.place_id} className="px-3 py-2 hover:bg-primary/10 cursor-pointer text-sm" onClick={() => {
                                    const details = s.address || {};
                                    const newLocations = [...formData.locations];
                                    newLocations[index].address = s.display_name;
                                    newLocations[index].doorNumber = details.house_number || '';
                                    newLocations[index].street = details.road || details.pedestrian || details.footway || '';
                                    newLocations[index].city = details.city || details.town || details.village || '';
                                    newLocations[index].state = details.state || '';
                                    newLocations[index].postcode = details.postcode || '';
                                    setFormData(prev => ({ ...prev, locations: newLocations }));
                                    setAddressSuggestions([]);
                                  }}>{s.display_name}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Door/Flat/Unit Number</label>
                            <input
                              type="text"
                              value={location.doorNumber || ''}
                              onChange={e => {
                                const newLocations = [...formData.locations];
                                newLocations[index].doorNumber = e.target.value;
                                setFormData(prev => ({ ...prev, locations: newLocations }));
                              }}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Street/Road</label>
                            <input
                              type="text"
                              value={location.street || ''}
                              onChange={e => {
                                const newLocations = [...formData.locations];
                                newLocations[index].street = e.target.value;
                                setFormData(prev => ({ ...prev, locations: newLocations }));
                              }}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">City/Town</label>
                            <input
                              type="text"
                              value={location.city || ''}
                              onChange={e => {
                                const newLocations = [...formData.locations];
                                newLocations[index].city = e.target.value;
                                setFormData(prev => ({ ...prev, locations: newLocations }));
                              }}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                            <input
                              type="text"
                              value={location.state || ''}
                              onChange={e => {
                                const newLocations = [...formData.locations];
                                newLocations[index].state = e.target.value;
                                setFormData(prev => ({ ...prev, locations: newLocations }));
                              }}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                            <input
                              type="text"
                              value={location.postcode || ''}
                              onChange={e => {
                                const newLocations = [...formData.locations];
                                newLocations[index].postcode = e.target.value;
                                setFormData(prev => ({ ...prev, locations: newLocations }));
                              }}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                          </div>
                        </>
                      ) : (
                        // Fallback for other countries: show all fields
                        <>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                            <input
                              type="text"
                              value={location.address || ''}
                              onChange={e => handleAddressInput(e.target.value, index)}
                              onBlur={() => setTimeout(() => setAddressSuggestions([]), 200)}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                            {activeAddressIndex === index && addressSuggestions.length > 0 && (
                              <ul className="absolute z-10 bg-white border border-gray-200 rounded shadow mt-1 w-full max-h-48 overflow-auto">
                                {addressSuggestions.map(s => (
                                  <li key={s.place_id} className="px-3 py-2 hover:bg-primary/10 cursor-pointer text-sm" onClick={() => {
                                    const details = s.address || {};
                                    const newLocations = [...formData.locations];
                                    newLocations[index].address = s.display_name;
                                    newLocations[index].doorNumber = details.house_number || '';
                                    newLocations[index].street = details.road || details.pedestrian || details.footway || '';
                                    newLocations[index].city = details.city || details.town || details.village || '';
                                    newLocations[index].state = details.state || '';
                                    newLocations[index].postcode = details.postcode || '';
                                    setFormData(prev => ({ ...prev, locations: newLocations }));
                                    setAddressSuggestions([]);
                                  }}>{s.display_name}</li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Door/Flat/Unit Number</label>
                            <input
                              type="text"
                              value={location.doorNumber || ''}
                              onChange={e => {
                                const newLocations = [...formData.locations];
                                newLocations[index].doorNumber = e.target.value;
                                setFormData(prev => ({ ...prev, locations: newLocations }));
                              }}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Street/Road</label>
                            <input
                              type="text"
                              value={location.street || ''}
                              onChange={e => {
                                const newLocations = [...formData.locations];
                                newLocations[index].street = e.target.value;
                                setFormData(prev => ({ ...prev, locations: newLocations }));
                              }}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">City/Town</label>
                            <input
                              type="text"
                              value={location.city || ''}
                              onChange={e => {
                                const newLocations = [...formData.locations];
                                newLocations[index].city = e.target.value;
                                setFormData(prev => ({ ...prev, locations: newLocations }));
                              }}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                            <input
                              type="text"
                              value={location.state || ''}
                              onChange={e => {
                                const newLocations = [...formData.locations];
                                newLocations[index].state = e.target.value;
                                setFormData(prev => ({ ...prev, locations: newLocations }));
                              }}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                            <input
                              type="text"
                              value={location.postcode || ''}
                              onChange={e => {
                                const newLocations = [...formData.locations];
                                newLocations[index].postcode = e.target.value;
                                setFormData(prev => ({ ...prev, locations: newLocations }));
                              }}
                              className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                            />
                          </div>
                        </>
                      )}
                    </div>
                    {['us', 'ca', 'au'].includes(getCountryCode()) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
                        <input
                          type="text"
                          value={location.state || ''}
                          onChange={e => {
                            const newLocations = [...formData.locations];
                            newLocations[index].state = e.target.value;
                            setFormData(prev => ({ ...prev, locations: newLocations }));
                          }}
                          className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
            {formData.has_multiple_locations === 'multiple' && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    locations: [...prev.locations, { name: '', address: '', storeID: generateId() }]
                  }));
                  setOpenLocationIndex(formData.locations.length);
                }}
                className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                + Add Another Location
              </motion.button>
            )}
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            {formData.roles.map((role, index) => (
              <div key={index} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role Name</label>
                  <input
                    type="text"
                    name={`roles.${index}.name`}
                    value={role.name}
                    onChange={(e) => {
                      const newRoles = [...formData.roles];
                      newRoles[index].name = e.target.value;
                      setFormData(prev => ({ ...prev, roles: newRoles }));
                    }}
                    className="w-full px-3 py-2 rounded-xl bg-white/80 border-2 border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/10 text-black transition-all duration-300 shadow-sm hover:shadow-md"
                    disabled={index < 2}
                    readOnly={index < 2}
                  />
                </div>
              </div>
            ))}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  roles: [...prev.roles, { name: '' }]
                }));
              }}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              + Add Role
            </motion.button>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Week Start Day</label>
              <select
                name="week_start_day"
                value={formData.week_start_day}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg bg-white/80 border border-gray-300 focus:border-secondary focus:ring-1 focus:ring-secondary text-black transition text-sm"
              >
                <option value="Sun">Sunday</option>
                <option value="Mon">Monday</option>
                <option value="Tue">Tuesday</option>
                <option value="Wed">Wednesday</option>
                <option value="Thu">Thursday</option>
                <option value="Fri">Friday</option>
                <option value="Sat">Saturday</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Shift Start</label>
                <input
                  type="time"
                  name="default_shift_start"
                  value={(() => {
                    // Convert 12h AM/PM to 24h for input value
                    const { h, m } = parseTime12(formData.default_shift_start);
                    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                  })()}
                  onChange={(e) => {
                    // Convert 24h time to 12h time with AM/PM
                    const time = e.target.value;
                    const [hours, minutes] = time.split(':');
                    const h = parseInt(hours, 10);
                    const ampm = h >= 12 ? 'PM' : 'AM';
                    const h12 = h % 12 || 12;
                    const timeStr = `${h12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
                    setFormData(prev => ({
                      ...prev,
                      default_shift_start: timeStr
                    }));
                  }}
                  className="w-full px-4 py-2 rounded-lg bg-white/80 border border-gray-300 focus:border-secondary focus:ring-1 focus:ring-secondary text-black transition text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Default Shift End</label>
                <input
                  type="time"
                  name="default_shift_end"
                  value={(() => {
                    // Convert 12h AM/PM to 24h for input value
                    const { h, m } = parseTime12(formData.default_shift_end);
                    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                  })()}
                  onChange={(e) => {
                    // Convert 24h time to 12h time with AM/PM
                    const time = e.target.value;
                    const [hours, minutes] = time.split(':');
                    const h = parseInt(hours, 10);
                    const ampm = h >= 12 ? 'PM' : 'AM';
                    const h12 = h % 12 || 12;
                    const timeStr = `${h12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
                    setFormData(prev => ({
                      ...prev,
                      default_shift_end: timeStr
                    }));
                  }}
                  className="w-full px-4 py-2 rounded-lg bg-white/80 border border-gray-300 focus:border-secondary focus:ring-1 focus:ring-secondary text-black transition text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Break Duration</label>
              <select
                name="default_break_duration"
                value={formData.default_break_duration}
                onChange={handleInputChange}
                className="w-full px-4 py-2 rounded-lg bg-white/80 border border-gray-300 focus:border-secondary focus:ring-1 focus:ring-secondary text-black transition text-sm"
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="60">60 minutes</option>
                <option value="custom">Custom</option>
              </select>
              {formData.default_break_duration === 'custom' && (
                <input
                  type="number"
                  name="custom_break_duration"
                  min={1}
                  max={120}
                  value={formData.custom_break_duration || ''}
                  onChange={e => {
                    let val = e.target.value.replace(/[^0-9]/g, '');
                    if (val !== '' && (parseInt(val, 10) < 1 || parseInt(val, 10) > 120)) return;
                    setFormData(prev => ({ ...prev, custom_break_duration: val }));
                  }}
                  placeholder="Enter minutes (max 120)"
                  className="mt-2 w-full px-4 py-2 rounded-lg bg-white/80 border border-gray-300 focus:border-secondary focus:ring-1 focus:ring-secondary text-black transition text-sm"
                />
              )}
            </div>
          </motion.div>
        );

      case 5:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <TeamMembers
              teamMembers={formData.team_members}
              roles={formData.roles}
              country={formData.country}
              errors={errors.team_members || {}}
              openIndex={openTeamMemberIndex}
              setOpenIndex={setOpenTeamMemberIndex}
              onChange={updated => setFormData(prev => ({ ...prev, team_members: updated }))}
              onAdd={() => {
                setFormData(prev => ({
                  ...prev,
                  team_members: [
                    ...prev.team_members,
                    {
                      name: '',
                      lastName: '',
                      pin: generatePin(),
                      phone: '',
                      level: '',
                      is_admin: false,
                    }
                  ]
                }));
                setOpenTeamMemberIndex(formData.team_members.length);
              }}
              onDelete={idx => {
                const newMembers = formData.team_members.filter((_, i) => i !== idx);
                setFormData(prev => ({ ...prev, team_members: newMembers }));
                setOpenTeamMemberIndex(0);
              }}
              onRegeneratePin={idx => {
                const newMembers = [...formData.team_members];
                newMembers[idx].pin = generatePin();
                setFormData(prev => ({ ...prev, team_members: newMembers }));
              }}
              adminName={formData.admin_name}
              adminPhone={formData.mobile_number}
            />
          </motion.div>
        );

      default:
        return null;
    }
  };

  const CompletionModal = () => (
    <AnimatePresence>
      {showCompletionModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaCheckCircle className="text-3xl text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-primary mb-2">Registration Complete!</h3>
              <p className="text-gray-600 mb-6">
                Your registration data has been logged to the console.
              </p>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setShowCompletionModal(false);
                  setCurrentStep(1);
                  setFormData({
                    business_name: '',
                    admin_name: '',
                    email: '',
                    mobile_number: '',
                    country: '',
                    business_type: '',
                    industry: '',
                    employee_count: '',
                    has_multiple_locations: 'single',
                    locations: [{ name: '', address: '' }],
                    roles: [{ name: 'Admin' }, { name: 'Manager' }],
                    week_start_day: 'Sun',
                    default_shift_start: '05:00 AM',
                    default_shift_end: '05:00 PM',
                    default_break_duration: '30',
                    team_members: [{
                      name: '',
                      lastName: '',
                      pin: Math.floor(1000 + Math.random() * 9000).toString(),
                      phone: '',
                      level: 'Admin',
                      is_admin: true,
                    }]
                  });
                }}
                className="w-full px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-secondary transition-colors"
              >
                Start New Registration
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const handleDeleteLocation = idx => {
    let newLocations = formData.locations.filter((_, i) => i !== idx);
    if (newLocations.length === 0) {
      newLocations = [{ name: '', address: '', storeID: generateId() }];
      setOpenLocationIndex(0);
    } else if (openLocationIndex >= newLocations.length) {
      setOpenLocationIndex(newLocations.length - 1);
    }
    setFormData(prev => ({ ...prev, locations: newLocations }));
  };

  // On step 5 load, ensure all team members have a PIN
  useEffect(() => {
    if (currentStep === 5) {
      setFormData(prev => {
        const team_members = prev.team_members.map(member => ({
          ...member,
          pin: member.pin && member.pin.length === 4 ? member.pin : generatePin()
        }));
        return { ...prev, team_members };
      });
    }
  }, [currentStep]);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {isLoading && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center bg-white rounded-2xl p-8 shadow-card-hover">
            <div className="w-12 h-12 border-4 border-secondary/20 border-t-secondary rounded-full animate-spin" />
            <div className="mt-4 text-center text-gray-700 font-semibold text-sm">
              {currentStep === 1 ? 'Verifying phone number...' : 'Registering your account...'}
            </div>
          </div>
        </div>
      )}

      {/* Hero header */}
      <div className="bg-hero-gradient text-white pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-hero-mesh" />
        <div className="absolute top-[10%] left-[15%] w-64 h-64 bg-secondary/10 rounded-full blur-3xl animate-pulse-glow" />
        <div className="absolute bottom-[10%] right-[10%] w-80 h-80 bg-accent/5 rounded-full blur-3xl animate-pulse-glow" style={{ animationDelay: '1.5s' }} />
        <div className="container mx-auto px-4 max-w-4xl relative z-10 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-extrabold mb-2"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Get Started with ClockIn Master
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white/60 text-sm"
          >
            Set up your account in just a few minutes
          </motion.p>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-4xl -mt-8 relative z-10 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-3xl shadow-card-hover p-6 md:p-8 border border-gray-100"
        >
          {/* Progress Stepper */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-6 px-2">
              {steps.map((step, i) => (
                <React.Fragment key={step.number}>
                  <div className="flex flex-col items-center relative z-10">
                    <motion.div
                      whileHover={{ scale: 1.08 }}
                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center transition-all duration-400 ${currentStep > step.number
                        ? 'bg-accent text-primary shadow-lg'
                        : currentStep === step.number
                          ? 'bg-secondary text-white shadow-glow'
                          : 'bg-gray-100 text-gray-400'
                        }`}
                    >
                      {currentStep > step.number ? (
                        <FaCheck className="text-sm sm:text-base" />
                      ) : (
                        <step.icon className="text-sm sm:text-base" />
                      )}
                    </motion.div>
                    <span className={`hidden sm:block text-xs mt-2 font-medium text-center whitespace-nowrap ${currentStep >= step.number ? 'text-gray-900' : 'text-gray-400'
                      }`}>{step.title}</span>
                  </div>
                  {i < steps.length - 1 && (
                    <div className="flex-1 h-0.5 mx-2 sm:mx-3 relative top-[-12px] sm:top-[-8px]">
                      <div className="h-full bg-gray-100 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-accent rounded-full"
                          initial={{ width: '0%' }}
                          animate={{ width: currentStep > step.number ? '100%' : '0%' }}
                          transition={{ duration: 0.5 }}
                        />
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
            <div className="text-center text-xs text-muted">
              Step {currentStep} of {steps.length}
            </div>
          </div>

          {/* Step Content */}
          <div className="mb-8">
            <motion.h2
              key={currentStep}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-2xl md:text-3xl font-bold text-gray-900 mb-2"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {steps[currentStep - 1].title}
            </motion.h2>
            {formData.admin_name && (
              <motion.p initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="text-muted text-sm mb-6">
                Hi, {formData.admin_name}! Let's set up your business.
              </motion.p>
            )}
            {renderStepContent()}
            {Object.values(errors).some(v => typeof v === 'string') && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl"
              >
                {Object.entries(errors).map(([key, value]) => {
                  if (typeof value === 'string') {
                    return <p key={key} className="text-red-600 text-sm">{value}</p>;
                  }
                  // skip objects (like team_members), since those are rendered inline per field
                  return null;
                })}
              </motion.div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-100">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleBack}
              disabled={currentStep === 1 || isRegistering}
              className="flex items-center gap-2 px-5 py-2.5 bg-gray-50 text-gray-600 rounded-xl text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 transition-all duration-300"
            >
              <FaArrowLeft className="text-xs" />
              Back
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleNext}
              disabled={isCheckingPhone || isRegistering}
              className="flex items-center gap-2 px-6 py-2.5 bg-secondary text-white rounded-xl text-sm font-semibold hover:bg-secondary/90 transition-all duration-300 shadow-glow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(isCheckingPhone || isRegistering) ? (
                <svg className="animate-spin w-4 h-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : null}
              {currentStep === 5 ? (isRegistering ? 'Registering...' : 'Complete Registration') : 'Continue'}
              {!isCheckingPhone && !isRegistering && <FaArrowRight className="text-xs" />}
            </motion.button>
          </div>
        </motion.div>
      </div>
      <CompletionModal />
    </div>
  );
};

export default RegistrationStepper; 